import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { DEFAULT_MODEL_SELECTION, MODEL_OPTIONS, ModelSelection } from '../../types';

interface ModelSelectorProps {
  value: ModelSelection;
  onChange: (model: ModelSelection) => void;
  disabled?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ value, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const selectedOption = MODEL_OPTIONS.find(option => option.value === value)
    || MODEL_OPTIONS.find(option => option.value === DEFAULT_MODEL_SELECTION)!;

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  return (
    <div ref={selectorRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        disabled={disabled}
        className="flex items-center gap-1 rounded-lg px-2 py-1 text-left text-xs text-text-primary dark:text-dark-text hover:bg-neutral dark:hover:bg-dark-accent disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Selected model: ${selectedOption.label}`}
      >
        <span className="max-w-[9rem] truncate font-medium sm:max-w-none">{selectedOption.label}</span>
        <FiChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 z-30 mb-2 w-64 overflow-hidden rounded-xl border border-neutral-dark bg-white shadow-lg dark:border-dark-border dark:bg-dark-secondary"
          role="listbox"
          aria-label="Choose model"
        >
          {MODEL_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-neutral dark:hover:bg-dark-accent"
              role="option"
              aria-selected={value === option.value}
            >
              <span>
                <span className="block text-sm font-medium text-text-primary dark:text-dark-text">{option.label}</span>
                <span className="block text-xs text-text-tertiary dark:text-dark-textSecondary">{option.subtitle}</span>
              </span>
              {value === option.value && <FiCheck size={17} className="mt-0.5 shrink-0 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
